/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useRef, useState } from 'react';

interface FrameData {
  frameNumber: number;
  url: string;
}

interface UseFrameLoaderOptions {
  apiUrl: string;
  onFramesLoaded?: (frames: FrameData[], fromCache: boolean) => void;
  onError?: (error: string) => void;
  onImagesProcessing?: (frames: FrameData[]) => void;
}

// Singleton worker instance shared across all components
let sharedWorker: Worker | null = null;
let workerRefCount = 0;

// Request tracking
const pendingRequests = new Map<string, (frames: FrameData[]) => void>();

// Global counter for unique hook instances
let hookInstanceCounter = 0;

export function useFrameLoader({
  apiUrl,
  onFramesLoaded,
  onError,
  onImagesProcessing,
}: UseFrameLoaderOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const hookInstanceId = useRef(++hookInstanceCounter);

  // Initialize worker on first use
  useEffect(() => {
    if (!sharedWorker) {
      sharedWorker = new Worker('/frameLoader.worker.js');

      // Handle messages from worker
      sharedWorker.addEventListener('message', event => {
        const { type, id, frames, error, fromCache } = event.data;

        switch (type) {
          case 'frames': {
            const callback = pendingRequests.get(id);
            if (callback) {
              callback(frames);
              if (!fromCache) {
                pendingRequests.delete(id);
              }
            } else {
              break;
            }
            break;
          }

          case 'images-processing': {
            // Handle frames that are still being processed (403 errors)
            if (onImagesProcessing) {
              onImagesProcessing(frames);
            }
            break;
          }

          case 'error': {
            const errorCallback = pendingRequests.get(`${id}:error`);
            if (errorCallback) {
              errorCallback([]);
              pendingRequests.delete(id);
              pendingRequests.delete(`${id}:error`);
            }
            break;
          }
        }
      });
    }

    workerRefCount++;

    return () => {
      workerRefCount--;
      // Don't terminate worker immediately - keep it alive for other components
      if (workerRefCount === 0) {
        setTimeout(() => {
          if (workerRefCount === 0 && sharedWorker) {
            sharedWorker.terminate();
            sharedWorker = null;
          }
        }, 5000); // Keep worker alive for 5 seconds after last component unmounts
      }
    };
  }, []);

  // Load frames function
  const loadFrames = useCallback(
    (
      storyId: string,
      videoId: string,
      frameNumbers: number[],
      onLoadError?: (error: string) => void,
    ) => {
      if (!sharedWorker || frameNumbers.length === 0) return;

      const requestId = `${storyId}:${videoId}:${
        hookInstanceId.current
      }:${++requestIdRef.current}`;
      setIsLoading(true);

      // Set up callback for this request
      pendingRequests.set(requestId, (frames: FrameData[]) => {
        setIsLoading(false);
        onFramesLoaded?.(frames, false);
      });

      // Track the error callback separately
      if (onLoadError) {
        const errorHandler = () => {
          setIsLoading(false);
          onLoadError('Failed to load frames');
        };
        // Store error handler with special key
        pendingRequests.set(`${requestId}:error`, errorHandler as any);
      }

      // Send request to worker
      sharedWorker.postMessage({
        type: 'loadFrames',
        id: requestId,
        apiUrl,
        storyId,
        videoId,
        frameNumbers,
      });
    },
    [apiUrl, onFramesLoaded],
  );

  // Clear cache for a specific video
  const clearCache = useCallback((storyId?: string, videoId?: string) => {
    if (!sharedWorker) return;

    sharedWorker.postMessage({
      type: 'clearCache',
      storyId,
      videoId,
    });
  }, []);

  return {
    loadFrames,
    clearCache,
    isLoading,
  };
}
